import { FC, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useMediaQuery, useTheme } from '@mui/material'
import localStore from '@service/local-store'
import useAdobeAnalytics from '.'
import useHeaderDetails from '@header/useHeaderDetails'
import { findByRoute } from '../../route/findRouteConfig'
import {
  PATIENT_BUSINESS_TAGS,
  PROVIDER_BUSINESS_TAGS,
} from '../../route/questionTags'
import routesConfig from '../../route'
import { TAGS } from '../../util/constant'
import { PROVIDERS } from '../../route.const'

const AnalyticsTracker: FC = () => {
  const location = useLocation()
  const { trackPage, trackLink } = useAdobeAnalytics()
  const { breakpoints } = useTheme()
  const isMobile = useMediaQuery(breakpoints.down('sm'))
  const isTablet = useMediaQuery(breakpoints.down('lg'))
  const { login, role, ipAddress, user } = useHeaderDetails()
  const lastTrackedRef = useRef<string>('')
  const lastLinkRef = useRef<string>('')

  useEffect(() => {
    localStore.set('isMobile', isMobile)
    localStore.set('isTablet', isTablet)
  }, [isMobile, isTablet])

  useEffect(() => {
    // Create a unique key for this route to prevent duplicate tracking
    const parts = location.pathname.split('/').filter(Boolean)
    const hasQuestionnaire = parts.includes('questionnaire')
    const currentRoute = findByRoute(routesConfig, location.pathname)
    const fullUrl = location.pathname + location.search
    const cmp = new URLSearchParams(location.search).get('cmp')

    // Dedupe link tracking to avoid multiple identical link events
    const linkKey = `${currentRoute?.pageName || 'Landing Page'}::${fullUrl}`
    if (lastLinkRef.current !== linkKey) {
      lastLinkRef.current = linkKey
      trackPage(
        {
          experienceType: isMobile ? 'mobile' : 'desktop',
          is404: false,
          language: 'en',
          name: currentRoute?.pageName || 'Landing Page',
          customAttributes: {
            group: 'Optum Match',
          },
        },
        {
          loginStatus: login ? 'logged in' : 'logged out',
          role: role,
          ip: ipAddress,
          displayName: user?.displayName ? user?.displayName : 'Guest user',
        },
        fullUrl,
        cmp ? { cmp } : undefined
      )
      trackLink(
        {
          name: currentRoute?.pageName || 'Landing Page',
          location: 'internal',
          type: 'url',
          destinationUrl: fullUrl,
        },
        {
          loginStatus: login ? 'logged in' : 'logged out',
          role: role,
          ip: ipAddress,
          displayName: user?.displayName ? user?.displayName : 'Guest user',
        }
      )
    }

    // For non-question pages, create the key immediately and check for duplicates
    const baseRouteKey = `${location.pathname}${location.search}${login}${role}`

    if (!hasQuestionnaire) {
      // For non-question pages, check immediately to prevent duplicates
      if (lastTrackedRef.current === baseRouteKey) {
        return
      }
      // Mark as tracked immediately for non-question pages to prevent race conditions
      lastTrackedRef.current = baseRouteKey
    }

    // Small delay to ensure SurveyIntro component has set window.__SECTION_BUSINESS_ID__
    const timeoutId = setTimeout(() => {
      // For question pages, include the analytics tag in the route key
      let routeKey = baseRouteKey

      if (hasQuestionnaire) {
        // Check for the analytics tag from Question component AFTER delay
        const questionTag = (window as any).__LAST_ANALYTICS_TAG__
        // Include the question tag to ensure branching questions are tracked separately
        routeKey = `${baseRouteKey}${questionTag || ''}`

        // Skip if we've already tracked this exact route with this tag
        if (lastTrackedRef.current === routeKey) {
          return
        }

        // Mark as tracked for question pages
        lastTrackedRef.current = routeKey
      }
      const root = parts[0]?.toLowerCase()
      const surveyIdx = parts.indexOf('survey')

      // Special case: If on paywall route, ONLY track paywall, don't track as search result
      if (
        location.pathname === '/patients/accesswall' ||
        location.pathname === '/patient/accesswall'
      ) {
        trackPage(
          {
            experienceType: isMobile ? 'mobile' : 'desktop',
            is404: false,
            language: 'en',
            name: TAGS.OM_PA_ACCESS_WALL,
            customAttributes: {
              group: 'Optum Match',
            },
          },
          {
            loginStatus: login ? 'logged in' : 'logged out',
            userType: role,
          },
          fullUrl,
          cmp ? { cmp } : undefined
        )
        return
      }

      let pageTag = ''

      // Check if this is a survey section path (e.g., /patients/survey/c1)
      const isSurveySection =
        surveyIdx !== -1 && parts.length > surveyIdx + 1 && !hasQuestionnaire

      // ALWAYS try route config FIRST - it has deriveTag logic that can map sections
      if (!pageTag) {
        const currentRoute = findByRoute(routesConfig, location.pathname)

        // For question pages, don't use the placeholder "Survey Review" tags - wait for override
        const isQuestionPlaceholder =
          hasQuestionnaire &&
          (currentRoute?.analyticsTag === TAGS.OM_PA_SURVEY_REVIEW ||
            currentRoute?.analyticsTag === TAGS.OM_CL_SURVEY_REVIEW)

        if (currentRoute?.analyticsTag && !isQuestionPlaceholder) {
          pageTag = currentRoute.analyticsTag
        } else if (
          currentRoute?.pageName &&
          currentRoute.pageName !== 'Survey Section' &&
          currentRoute.pageName !== 'Survey Intro'
        ) {
          pageTag = currentRoute.pageName
        } else if (isSurveySection && !pageTag) {
          // Route config didn't return a tag for survey section, try manual lookup with business ID from window
          const businessId = (window as any).__SECTION_BUSINESS_ID__
          const isPatient = root === 'patients' || root === 'patient'
          const isProvider =
            root === 'providers' ||
            root === 'provider' ||
            root === 'clinician' ||
            root === 'clinicians'

          if (businessId) {
            const normalizedBusinessId = businessId.toLowerCase()
            if (isPatient && PATIENT_BUSINESS_TAGS[normalizedBusinessId]) {
              pageTag = PATIENT_BUSINESS_TAGS[normalizedBusinessId]
            } else if (
              isProvider &&
              PROVIDER_BUSINESS_TAGS[normalizedBusinessId]
            ) {
              pageTag = PROVIDER_BUSINESS_TAGS[normalizedBusinessId]
            }
          } else {
            // Business ID not set yet - wait a bit longer and retry
            setTimeout(() => {
              const retryBusinessId = (window as any).__SECTION_BUSINESS_ID__

              if (retryBusinessId) {
                const normalizedBusinessId = retryBusinessId.toLowerCase()
                let retryPageTag = ''

                if (isPatient && PATIENT_BUSINESS_TAGS[normalizedBusinessId]) {
                  retryPageTag = PATIENT_BUSINESS_TAGS[normalizedBusinessId]
                } else if (
                  isProvider &&
                  PROVIDER_BUSINESS_TAGS[normalizedBusinessId]
                ) {
                  retryPageTag = PROVIDER_BUSINESS_TAGS[normalizedBusinessId]
                }

                if (retryPageTag) {
                  // Track the page with the correct tag
                  trackPage(
                    {
                      experienceType: isMobile ? 'mobile' : 'desktop',
                      is404: false,
                      language: 'en',
                      name: retryPageTag,
                      customAttributes: {
                        group: 'Optum Match',
                      },
                    },
                    {
                      loginStatus: login ? 'logged in' : 'logged out',
                      userType: role,
                    },
                    fullUrl,
                    cmp ? { cmp } : undefined
                  )
                }
              }
            }, 200)

            // Skip tracking for now, the retry will handle it
            return
          }
        }
      }

      // Role-based tag mapping: if clinician is logged in, use clinician-specific tags for static pages
      const isClinician = login && role === PROVIDERS

      if (isClinician) {
        // Map generic static page tags to clinician-specific variants
        const tagMapping: { [key: string]: string } = {
          om_settings: TAGS.OM_CL_SETTINGS,
          om_about_us: TAGS.OM_CL_ABOUT_US,
          om_privacy_policy: TAGS.OM_CL_PRIVACY_POLICY,
          om_terms_of_use: TAGS.OM_CL_TERMS_OF_USE,
          om_faq: TAGS.OM_CL_FAQ,
          om_contact_us: TAGS.OM_CL_CLINICAL_ASSISTANCE,
          om_homepage: TAGS.OM_CL_HOME,
        }

        if (tagMapping[pageTag]) {
          pageTag = tagMapping[pageTag]
        }
      }

      // Override for individual survey questions once businessId tag is emitted from Question component
      // This applies to both patient and provider survey questions
      // CRITICAL: Only apply override when on a QUESTION page (has 'questionnaire' in URL), NOT on section pages
      const override = (window as any).__LAST_ANALYTICS_TAG__

      const isProviderSurvey =
        surveyIdx !== -1 &&
        (root === 'providers' ||
          root === 'provider' ||
          root === 'clinician' ||
          root === 'clinicians')
      const isPatientSurvey =
        surveyIdx !== -1 && (root === 'patients' || root === 'patient')

      // For question pages, ONLY track if we have the override tag from Question component
      if (hasQuestionnaire && (isProviderSurvey || isPatientSurvey)) {
        if (override && typeof override === 'string') {
          // Use the override tag from Question component
          pageTag = override
        } else {
          // Question page but override not set yet - skip tracking completely
          // The Question component will trigger a re-render when it sets the tag
          return
        }
      } else if (!hasQuestionnaire && override) {
        // Clear stale override when on section pages (not questionnaire)
        delete (window as any).__LAST_ANALYTICS_TAG__
      }

      // Final check - don't track if we still have no pageTag
      if (!pageTag) {
        return
      }

      trackPage(
        {
          experienceType: isMobile ? 'mobile' : 'desktop',
          is404: false,
          language: 'en',
          name: pageTag,
          customAttributes: {
            group: 'Optum Match',
          },
        },
        {
          loginStatus: login ? 'logged in' : 'logged out',
          userType: role,
        },
        fullUrl,
        cmp ? { cmp } : undefined
      )

      // Mark this route as tracked to prevent duplicates
      lastTrackedRef.current = routeKey
    }, 200) // 200ms delay to ensure SurveyIntro has set business ID in window

    // Cleanup timeout on unmount or when dependencies change
    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMobile,
    location.pathname,
    location.search,
    trackPage,
    trackLink,
    login,
    role,
    ipAddress,
    user,
  ])

  return null
}

export default AnalyticsTracker
